import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { AppClaimLive, AppClaimsLiveResponse, DeleteClaimsPlan, Device } from '../types';
import { APP_RECORD_DELETE_EFFECTS } from '../utils/lifecycleWording';
import Button from './ui/Button';
import { Checkbox } from './pilot-ui/checkbox';
import ConfirmDialog from './ConfirmDialog';

interface DeviceAppClaimsPanelProps {
  device: Device;
}

/**
 * Plan 94 — App Claims tab. Lists every live Firebase app registration for a
 * device (GET /fb/app-claims/:serial), flags the current one + duplicates +
 * orphaned/empty branches, and — for firestore_write users — lets the operator
 * select dead registrations and delete them (POST /fb/app-claims/:serial/delete),
 * optionally cascading to the now-empty HVAC/location docs.
 */
const STATUS_LABEL: Record<string, string> = {
  current: 'Single current registration',
  ambiguous: 'Duplicate registrations. The row tagged CURRENT is the one the system uses; amber cells mark where the other registrations differ from it.',
  stale: 'Current registration has orphaned/missing parent data',
  missing: 'Not registered in the MyFluxHive app',
};

const distinct = (claims: AppClaimLive[], key: keyof AppClaimLive) =>
  new Set(claims.map((c) => c[key]).filter(Boolean)).size;
const shortId = (id: string | null) => (id ? id.slice(0, 8) : '—');
const isOrphan = (c: AppClaimLive) => !c.owner_exists || !c.location_exists || !c.hvac_exists;
/** FS-914: the server leaves out an earlier household's details unless the caller has root scope. */
const ownerLabel = (c: AppClaimLive) =>
  c.owner_email || c.owner_name || (c.details_hidden ? 'Earlier registration' : 'Unknown');
const HIDDEN_DETAILS_TITLE =
  "An earlier registration of this Clip. Earlier households' contact details are shown only to accounts with root scope.";

const DeviceAppClaimsPanel: React.FC<DeviceAppClaimsPanelProps> = ({ device }) => {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('firestore_write');
  const serial = device.name;

  const [data, setData] = useState<AppClaimsLiveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cascade, setCascade] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [plan, setPlan] = useState<DeleteClaimsPlan | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const previewRequest = useRef(0);
  const readRequest = useRef(0);
  const noticeRef = useRef<HTMLParagraphElement>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (afterDeletion = false) => {
    const request = ++readRequest.current;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<AppClaimsLiveResponse>(
        `${config.firebaseApiUrl}/fb/app-claims/${encodeURIComponent(serial)}`,
      );
      if (request !== readRequest.current) return;
      setData(res.data);
      setSelected(new Set());
      setConfirming(false);
    } catch (err) {
      if (request !== readRequest.current) return;
      console.error('Failed to load app claims:', err);
      setError(afterDeletion ? 'Registrations were deleted, but app claims could not be refreshed.' : 'Failed to load app claims');
    } finally {
      if (request === readRequest.current) setLoading(false);
    }
  }, [serial]);

  useEffect(() => { load(); }, [load]);

  const current = useMemo(() => data?.claims.find((c) => c.is_current), [data]);

  if (loading && !data) return <p className="text-muted-foreground">Loading app claims…</p>;
  if (error && !data) return <p role="alert" className="text-status-error-foreground">{error}</p>;
  if (!data) return null;

  const { claims } = data;
  const claimByPath = new Map(claims.map((c) => [c.path, c]));
  const isWarn = data.status === 'ambiguous' || data.status === 'stale';

  const differs = (c: AppClaimLive, key: keyof AppClaimLive) =>
    claims.length > 1 && current ? c[key] !== current[key] : false;
  const cellCls = (c: AppClaimLive, key: keyof AppClaimLive) =>
    `py-2 pr-4 align-top ${differs(c, key) ? 'bg-status-warning ring-1 ring-inset ring-status-warning-border' : ''}`;

  const toggle = (path: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });

  const deleteUrl = `${config.firebaseApiUrl}/fb/app-claims/${encodeURIComponent(serial)}/delete`;

  // Ask the server exactly what a delete would remove (no writes), so the confirm
  // dialog shows the precise plan.
  const preview = async (cascadeNow: boolean) => {
    const request = ++previewRequest.current;
    setPreviewing(true);
    setPlan(null);
    setError(null);
    try {
      const res = await axiosInstance.post<{ plan: DeleteClaimsPlan }>(deleteUrl, {
        device_paths: Array.from(selected),
        cascade_empty_parents: cascadeNow,
        dry_run: true,
      });
      if (request === previewRequest.current) setPlan(res.data.plan);
    } catch (err) {
      console.error('Preview failed:', err);
      if (request === previewRequest.current) setError('Failed to preview delete');
    } finally {
      if (request === previewRequest.current) setPreviewing(false);
    }
  };

  const openConfirm = () => {
    setNotice(null);
    setConfirming(true);
    preview(cascade);
  };
  const setCascadeAndPreview = (next: boolean) => {
    setCascade(next);
    preview(next);
  };
  const closeConfirm = () => {
    previewRequest.current += 1;
    setConfirming(false);
    setPlan(null);
    setError(null);
  };

  const doDelete = async () => {
    // A refresh begun before this mutation can only describe the old rows.
    readRequest.current += 1;
    setDeleting(true);
    setError(null);
    const paths = Array.from(selected);
    try {
      await axiosInstance.post(deleteUrl, {
        device_paths: paths,
        cascade_empty_parents: cascade,
      });
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Delete failed');
      setDeleting(false);
      setLoading(false);
      return;
    }
    setPlan(null);
    setSelected(new Set());
    setConfirming(false);
    setData((previous) => previous && {
      ...previous,
      claims: previous.claims.filter((claim) => !paths.includes(claim.path)),
    });
    setNotice('Registration deletion completed.');
    setDeleting(false);
    await load(true);
  };

  return (
    <div>
      <div className="mb-3 flex items-center gap-3">
        <Button variant="secondary" onClick={() => { void load(); }} disabled={loading} title="Reload app registrations for this device">Refresh app claims</Button>
        {loading && <p role="status" className="text-sm text-muted-foreground">Loading app claims…</p>}
      </div>
      {notice && <p ref={noticeRef} tabIndex={-1} role="status" className="mb-3 text-sm text-foreground">{notice}</p>}
      {error && !confirming && <p role="alert" className="mb-3 text-destructive">{error}</p>}
      {(!notice || (!loading && !error)) && <>
      <div className={`mb-3 rounded-md border p-3 text-sm ${isWarn ? 'border-status-warning-border bg-status-warning text-status-warning-foreground' : 'border-border bg-muted text-muted-foreground'}`}>
        <span className="font-medium capitalize">{data.status}</span>
        {' — '}
        {STATUS_LABEL[data.status] ?? data.status}
      </div>

      {claims.length > 1 && (
        <p className="mb-3 text-sm text-gray-600">
          {claims.length} registrations · {distinct(claims, 'uid')} owner(s) ·{' '}
          {distinct(claims, 'location_id')} location(s) · {distinct(claims, 'hvac_id')} HVAC system(s).
          Amber cells differ from the current registration.
        </p>
      )}

      {claims.length === 0 ? (
        <p className="text-muted-foreground">No Firebase app registrations for this device.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm leading-5">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                {canWrite && <th className="py-2 pr-3 font-medium w-8"></th>}
                <th className="py-2 pr-4 font-medium">Owner</th>
                <th className="py-2 pr-4 font-medium">Location</th>
                <th className="py-2 pr-4 font-medium">HVAC</th>
                <th className="py-2 pr-4 font-medium">Filter</th>
                <th className="py-2 pr-4 font-medium">Updated</th>
                <th className="py-2 pr-4 font-medium">Flags</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => (
                <tr key={c.path} className={`border-b last:border-0 ${c.is_current ? 'ring-2 ring-status-info-border' : ''}`}>
                  {canWrite && (
                    <td className="py-2 pr-3 align-top">
                      <label className="inline-flex items-center gap-1">
                        <Checkbox
                          checked={selected.has(c.path)}
                          onCheckedChange={() => toggle(c.path)}
                          aria-label={`Select registration ${shortId(c.hvac_id)}`}
                          title="Select this registration for deletion preview"
                        />
                        <span className="sr-only">Select registration {shortId(c.hvac_id)}</span>
                      </label>
                    </td>
                  )}
                  <td className={cellCls(c, 'uid')}>
                    <div
                      className="font-medium flex items-center gap-2"
                      title={c.details_hidden ? HIDDEN_DETAILS_TITLE : undefined}
                    >
                      {ownerLabel(c)}
                      {c.is_current && (
                        <span className="text-[10px] font-semibold uppercase border border-status-info-border text-status-info-foreground bg-status-info rounded-sm px-1">current</span>
                      )}
                    </div>
                    {c.owner_name && c.owner_email && <div className="text-xs text-muted-foreground">{c.owner_name}</div>}
                    <div className="text-[11px] font-mono text-foreground" title={c.uid ?? ''}>uid {shortId(c.uid)}</div>
                  </td>
                  <td className={cellCls(c, 'location_id')}>
                    <div>{c.location_name || '—'}</div>
                    <div className="text-[11px] font-mono text-foreground" title={c.location_id ?? ''}>{shortId(c.location_id)}</div>
                  </td>
                  <td className={cellCls(c, 'hvac_id')}>
                    <div>{c.hvac_name || '—'}</div>
                    <div className="text-[11px] font-mono text-foreground" title={c.hvac_id ?? ''}>{shortId(c.hvac_id)}</div>
                  </td>
                  <td className={cellCls(c, 'filter_summary')}>{c.filter_summary || '—'}</td>
                  <td className="py-2 pr-4 align-top whitespace-nowrap text-gray-600">
                    {c.update_time ? c.update_time.slice(0, 10) : '—'}
                  </td>
                  <td className="py-2 pr-4 align-top">
                    <div className="flex flex-col gap-1">
                      {isOrphan(c) && (
                        <span className="text-[10px] font-semibold uppercase text-status-error-foreground">orphaned</span>
                      )}
                      {c.hvac_other_device_count === 0 && (
                        <span className="text-[10px] text-muted-foreground" title="No other devices under this HVAC">empties HVAC</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {canWrite && claims.length > 0 && (
            <div className="mt-4 border-t pt-3">
              <Button
                variant="secondary"
                disabled={selected.size === 0}
                aria-describedby={selected.size === 0 ? 'claim-selection-reason' : undefined}
                onClick={openConfirm}
                title="Preview exactly what deleting the selected registrations would remove"
              >
                Delete selected ({selected.size})
              </Button>
              {selected.size === 0 && <p id="claim-selection-reason" className="mt-1 text-xs text-muted-foreground">Select a registration to preview deletion.</p>}
            </div>
          )}
        </div>
      )}
      </>}
      <ConfirmDialog
        open={confirming}
        title="Delete app registrations"
        tone="danger"
        confirmLabel="Confirm delete"
        confirmTitle="Permanently delete the listed Firestore documents"
        confirmDisabled={previewing || !plan}
        disabledReason={error ? 'Retry the deletion preview before confirming.' : 'Wait for the deletion preview before confirming.'}
        submitting={deleting}
        error={error}
        returnFocusTo={noticeRef}
        message="This will permanently delete from Firestore:"
        onConfirm={doDelete}
        onCancel={closeConfirm}
      >
        <label className="flex items-center gap-2">
          <Checkbox checked={cascade} onCheckedChange={setCascadeAndPreview} title="Include HVAC and location documents left empty by this deletion" />
          Also delete HVAC/location docs that become empty
        </label>
        {previewing || !plan ? (
          <div className="mt-2">
            <p className="text-muted-foreground">{previewing ? 'Computing exactly what will be removed…' : 'The deletion preview is unavailable.'}</p>
            {error && <Button variant="secondary" size="sm" onClick={() => preview(cascade)} title="Try computing the deletion preview again">Retry preview</Button>}
          </div>
        ) : (
          <ul className="mt-2 space-y-2">
            <li>
              <span className="font-medium">{plan.device_paths.length} device registration(s):</span>
              <ul className="ml-4 mt-1 list-disc space-y-0.5">
                {plan.device_paths.map((path) => {
                  const claim = claimByPath.get(path);
                  return (
                    <li key={path}>
                      {claim ? `${ownerLabel(claim)} · HVAC ${shortId(claim.hvac_id)}` : path}
                      {path === current?.path && <span> ⚠ this is the current registration</span>}
                    </li>
                  );
                })}
              </ul>
            </li>
            {plan.hvac_paths.length > 0 && (
              <li>
                {plan.hvac_paths.length} empty HVAC system(s):
                <ul className="ml-4 list-disc break-all font-mono text-xs">
                  {plan.hvac_paths.map((path) => <li key={path}>{path}</li>)}
                </ul>
              </li>
            )}
            {plan.location_paths.length > 0 && (
              <li>
                {plan.location_paths.length} empty location(s):
                <ul className="ml-4 list-disc break-all font-mono text-xs">
                  {plan.location_paths.map((path) => <li key={path}>{path}</li>)}
                </ul>
              </li>
            )}
          </ul>
        )}
        <div data-testid="claims-delete-effects" className="mt-3 text-xs">
          <p className="font-medium">What this does to the Clip:</p>
          <ul className="ml-4 mt-1 list-disc space-y-0.5">{APP_RECORD_DELETE_EFFECTS.map((line) => <li key={line}>{line}</li>)}</ul>
        </div>
        <p className="mt-3 text-xs">
          No user accounts or profiles (<span className="font-mono">users/{'{uid}'}</span>) and no Firebase Authentication accounts are deleted — only the device registration docs above{cascade ? ' and any branch left empty.' : '.'}
        </p>
      </ConfirmDialog>
    </div>
  );
};

export default DeviceAppClaimsPanel;
